/**
 * 账号等级中文名（避免把 admin/L1 这类原值直接展示给用户）。
 *
 * 各端曾各写一份（hospital/config.ts、ops/session.ts、system/session.ts），
 * 新增等级时容易漏改；统一放这里，端内保持 re-export 以免批量改调用点。
 */
export const LEVEL_LABELS: Record<string, string> = {
  admin: '平台管理员',
  sys: '系统管理员',
  L0: 'L0 临时账号',
  L1: 'L1 院级管理',
  L2: 'L2 科室管理',
  L3: 'L3 业务岗位',
  L4: 'L4 只读账号'
};

export const levelLabel = (level?: string | null): string =>
  !level ? '-' : LEVEL_LABELS[level] ?? level;

/**
 * 审批流状态（`cfg_approval_flow.status`）。
 *
 * 曾在 ops 与 hospital 各写一份且取值分叉（DRAFT 一端 gold、一端 default），
 * 同一个业务对象在两个端看到的颜色语义不同，排查问题时会产生误判。
 */
export const APPROVAL_FLOW_STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'gold' },
  PUBLISHED: { text: '已发布', color: 'green' },
  DISABLED: { text: '已停用', color: 'default' }
};

export const approvalFlowStatusMeta = (status?: string | null): { text: string; color: string } =>
  status ? (APPROVAL_FLOW_STATUS_META[status] ?? { text: status, color: 'default' }) : { text: '-', color: 'default' };

/** 审批节点类型：必须与后端 `ApprovalEngine.NodeTypes` 完全一致（SIGN 之类别名后端只做存量纠偏）。 */
export const APPROVAL_NODE_TYPE_LABELS: Record<string, string> = {
  APPROVE: '单签审批',
  COUNTERSIGN: '会签',
  OR_SIGN: '或签',
  CC: '抄送'
};

export interface LabeledOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * 审批人来源：与后端 `ApprovalEngine.ApproveScopes` 一致。
 *
 * DYNAMIC / EXPRESSION / AI_ASSIGN 后端只做「按配置的角色/等级/指定人」静态降级，
 * 真正的动态指派尚未实现，故置灰不可新选，但保留在选项里让存量配置能正常回显。
 */
export const APPROVAL_SCOPE_OPTIONS: LabeledOption[] = [
  { value: 'DEPT_HEAD', label: '科室负责人' },
  { value: 'ORG_ADMIN', label: '机构管理员' },
  { value: 'ROLE', label: '指定角色' },
  { value: 'USER', label: '指定人员' },
  { value: 'LEVEL', label: '按账号等级' },
  { value: 'SUPERVISOR_ORG', label: '上级监管部门' },
  { value: 'GOV', label: '卫健监管' },
  { value: 'DYNAMIC', label: '按组织层级动态（未实现，仅存量兼容）', disabled: true },
  { value: 'EXPRESSION', label: '表达式解析（未实现，仅存量兼容）', disabled: true },
  { value: 'AI_ASSIGN', label: '智能推荐（未实现，仅存量兼容）', disabled: true }
];

export const APPROVAL_NODE_TYPE_OPTIONS: LabeledOption[] = Object.entries(APPROVAL_NODE_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const APPROVAL_SCOPE_LABELS: Record<string, string> = Object.fromEntries(
  APPROVAL_SCOPE_OPTIONS.map((option) => [option.value, option.label])
);

const labelOf = (labels: Record<string, string>, value?: string | null): string =>
  !value ? '-' : (labels[value] ?? value);

/** 详情/列表展示：按码翻译，未知存量值原样显示，不假装它还在枚举里。 */
export const approvalNodeTypeLabel = (value?: string | null): string =>
  labelOf(APPROVAL_NODE_TYPE_LABELS, value);

export const approvalScopeLabel = (value?: string | null): string =>
  labelOf(APPROVAL_SCOPE_LABELS, value);

/**
 * 结算流水枚举（与后端 `SettlementService.PayMethods` / `InvoiceTypes` 对齐）。
 * 曾在 ops 的 settle-enums.ts 与 hospital 的 SettleInvoice.tsx 各存一份逐字重复的副本。
 */
export const PAY_METHOD_LABELS: Record<string, string> = {
  BANK: '银行汇款',
  TRANSFER: '转账',
  ACCEPTANCE: '承兑汇票',
  OTHER: '其他'
};

/** `wl_invoice.invoice_type`（票种）。 */
export const INVOICE_TYPE_LABELS: Record<string, string> = {
  SPECIAL_VAT: '增值税专用发票',
  GENERAL_VAT: '增值税普通发票',
  ELECTRONIC: '电子发票'
};

/** `wl_invoice.status`（发票状态）。 */
export const INVOICE_STATUS_LABELS: Record<string, string> = {
  ISSUED: '已开具',
  RECEIVED: '已收票',
  RED_FLUSHED: '已红冲',
  INVALID: '已作废'
};

export const PAY_METHOD_OPTIONS: LabeledOption[] = Object.entries(PAY_METHOD_LABELS).map(([value, label]) => ({
  value,
  label
}));

export const INVOICE_TYPE_OPTIONS: LabeledOption[] = Object.entries(INVOICE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label
}));

export const payMethodLabel = (value?: string | null): string => labelOf(PAY_METHOD_LABELS, value);

/**
 * 结算单状态（`wl_settle.settle_status`）的**并集**。
 *
 * 曾在 ops / hospital（SettleList、SettleInvoice）/ supplier 各写一份且取值与配色分叉：
 * 同一个 PART_PAID 一端 orange、一端 cyan；hospital 用 PENDING，ops 用 SENT。
 * 这里取并集并统一配色，各端只做「按码翻译」，未知存量值原样显示。
 */
export const SETTLE_STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  GENERATED: { text: '已生成', color: 'processing' },
  PENDING: { text: '待确认', color: 'gold' },
  SENT: { text: '待确认', color: 'gold' },
  CONFIRMED: { text: '已确认', color: 'blue' },
  INVOICED: { text: '已开票', color: 'cyan' },
  PART_PAID: { text: '部分付款', color: 'orange' },
  PAID: { text: '已付清', color: 'green' },
  SETTLED: { text: '已结清', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  DISPUTED: { text: '有争议', color: 'red' },
  RE_ADJUSTED: { text: '已调账', color: 'purple' },
  WRITTEN_OFF: { text: '已核销', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' }
};

export const settleStatusMeta = (status?: string | null): { text: string; color: string } =>
  status ? (SETTLE_STATUS_META[status] ?? { text: status, color: 'default' }) : { text: '-', color: 'default' };

export const invoiceTypeLabel = (value?: string | null): string => labelOf(INVOICE_TYPE_LABELS, value);

export const invoiceStatusLabel = (value?: string | null): string => labelOf(INVOICE_STATUS_LABELS, value);
